<template>
	<div class="calc-pdf-calculation-page">
		<div class="calc-pdf-calculation-page__strip"></div>

		<div class="calc-pdf-calculation-page__content">
			<h1 class="calc-pdf-calculation-page__title">
				{{ $t('calculation_pdf.mini-calculator_page_2-heading') }}
			</h1>
			<p class="calc-pdf-calculation-page__intro">
				{{
					calculationData.hasCustomProjectName
						? $t('calculation_pdf.mini-calculator_page_2-intro_with_named_project', { projectName: calculationData.projectName })
						: $t('calculation_pdf.mini-calculator_page_2-intro')
				}}
			</p>

			<table class="calc-pdf-calculation-page__table">
				<thead>
					<tr class="calc-pdf-calculation-page__table-header-row">
						<th
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--label calc-pdf-calculation-page__table-cell--left-align"
						>
							{{ $t('calculation_pdf.mini-calculator_page_2-financing_solution') }}
						</th>
						<th
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--group"
							colspan="3"
						>
							{{ $t('calculation_pdf.mini-calculator_page_2-pay_per_use') }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--label"
						>
							{{ $t('calculation_pdf.mini-calculator_page_2-flexibility_level') }}
						</td>
						<td
							v-for="level in levels"
							:key="`level-${level.key}`"
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--center"
						>
							{{ $t(`calculation_pdf.${level.labelKey}`) }}
						</td>
					</tr>
					<tr class="calc-pdf-calculation-page__table-row--mint">
						<td
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--label"
						>
							{{ $t('calculation_pdf.mini-calculator_page_2-base_fee_per_month') }}
						</td>
						<td
							v-for="level in levels"
							:key="`base-${level.key}`"
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--value"
						>
							{{ displayAsCurrency(level.baseFee) }}
						</td>
					</tr>
					<tr>
						<td
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--label"
						>
							{{ $t(calculationData.billingLabelKey || '§calc.step1.billing_options.hours') }}
						</td>
						<td
							v-for="level in levels"
							:key="`hour-${level.key}`"
							class="calc-pdf-calculation-page__table-cell calc-pdf-calculation-page__table-cell--value"
						>
							{{ displayAsCurrency(level.perUsage) }}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="calc-pdf-calculation-page__columns">
				<div class="calc-pdf-calculation-page__column">
					<div class="calc-pdf-calculation-page__column-heading">
						{{ $t('calculation_pdf.mini-calculator_page_2-general_assumptions') }}
					</div>
					<ul class="calc-pdf-calculation-page__list">
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_1') }} {{ displayAsCurrency(calculationData.amount, 0) }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_2') }} {{ formatNumber(calculationData.expectedUtilization, locale) }} {{ $t(`§calc.step1.billing_options.${calculationData.billingUnit}`) }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_3', {years: calculationData.duration}) }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_4') }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_5') }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_6') }} {{ displayAsCurrency(calculationData.residualValue, 0) }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-assumption_7') }} {{ calculationData.countryTranslated || calculationData.country }}</li>
					</ul>
				</div>
				<div class="calc-pdf-calculation-page__column">
					<div class="calc-pdf-calculation-page__column-heading">
						{{ $t('calculation_pdf.mini-calculator_page_2-terms_of_contract') }}
					</div>
					<ul class="calc-pdf-calculation-page__list">
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-condition_1') }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-condition_2') }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-condition_3') }}</li>
						<li>{{ $t('calculation_pdf.mini-calculator_page_2-condition_4') }}</li>
					</ul>
				</div>
			</div>

			<p class="calc-pdf-calculation-page__footnote">
				{{ $t('calculation_pdf.mini-calculator_page_2-footnote') }}
			</p>
		</div>

		<footer class="calc-pdf-calculation-page__footer">
			<span class="calc-pdf-calculation-page__page-number">2</span>
			<span class="calc-pdf-calculation-page__confidentiality">{{
				$t('calculation_pdf.mini-calculator_page_2-confidentiality')
			}}</span>
			<LinxfourLogo class="calc-pdf-calculation-page__logo" />
		</footer>
	</div>
</template>

<script>
import LinxfourLogo from './LinxfourLogo.vue'
import { formatNumber } from '../../helpers/format.js'

export default {
	name: 'CalcPdfCalculationPage',

	components: {
		LinxfourLogo,
	},

	props: {
		calculationData: {
			type: Object,
			required: true,
		},
		locale: {
			type: String,
			default: 'en-GB',
		},
	},

	computed: {
		levels() {
			const flex = this.calculationData.flex || {}
			return [
				{ key: 'high',   labelKey: 'mini-calculator_page_2-flexibility_high',   baseFee: flex.high?.baseFee   ?? 0, perUsage: flex.high?.perUsage   ?? 0 },
				{ key: 'medium', labelKey: 'mini-calculator_page_2-flexibility_medium', baseFee: flex.medium?.baseFee ?? 0, perUsage: flex.medium?.perUsage ?? 0 },
				{ key: 'low',    labelKey: 'mini-calculator_page_2-flexibility_low',    baseFee: flex.low?.baseFee    ?? 0, perUsage: flex.low?.perUsage    ?? 0 },
			]
		},
	},

	methods: {
		formatNumber,
	},
}
</script>

<style scoped>
.calc-pdf-calculation-page {
	--color-primary-200: #defdf3;
	--color-primary-300: #c7f6de;
	--color-primary-500: #55e38e;
	--color-secondary-900: #040f0f;
	--color-neutral-100: #ffffff;
	--text-dark-muted: #62817f;
	--font-family-sans: 'TWK Lausanne', sans-serif;

	box-sizing: border-box;
	position: relative;
	width: 1750px;
	height: 1000px;
	background-color: var(--color-neutral-100);
	color: var(--color-secondary-900);
	font-family: var(--font-family-sans);
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.calc-pdf-calculation-page__strip {
	position: absolute;
	top: 0;
	left: 0;
	width: 36px;
	height: 100%;
	background-color: var(--color-primary-500);
	background-image: url('/calculator-assets/fe_background.png');
	background-size: 1750px 1000px;
	background-position: 0 0;
	background-repeat: no-repeat;
}

.calc-pdf-calculation-page__content {
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 56px 80px 80px;
}

.calc-pdf-calculation-page__title {
	font-size: 66px;
	font-weight: 700;
	margin: 0 0 18px;
}

.calc-pdf-calculation-page__intro {
	font-size: 24px;
	line-height: 1.4;
	color: var(--text-dark-muted);
	margin: 0 0 48px;
	max-width: 1380px;
}

.calc-pdf-calculation-page__table {
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 48px;
	font-size: 24px;
	table-layout: fixed;
}

.calc-pdf-calculation-page__table-header-row {
	background-color: var(--color-primary-300);
}

.calc-pdf-calculation-page__table-cell {
	padding: 16px 22px;
	border-bottom: 2px solid var(--color-primary-500);
}

.calc-pdf-calculation-page__table-cell--label {
	font-weight: 500;
	width: 36%;
}

.calc-pdf-calculation-page__table-cell--left-align {
	text-align: left;
}

.calc-pdf-calculation-page__table-cell--group {
	font-weight: 500;
	text-align: center;
}

.calc-pdf-calculation-page__table-cell--center {
	text-align: center;
	border-left: 2px solid var(--color-primary-500);
	width: calc(64% / 3);
}

.calc-pdf-calculation-page__table-cell--value {
	text-align: center;
	font-weight: 300;
	border-left: 2px solid var(--color-primary-500);
	width: calc(64% / 3);
}

.calc-pdf-calculation-page__table-row--mint {
	background-color: var(--color-primary-200);
}

.calc-pdf-calculation-page__columns {
	display: flex;
	gap: 72px;
	margin-bottom: 48px;
}

.calc-pdf-calculation-page__column {
	flex: 1;
	font-size: 20px;
	line-height: 1.45;
}

.calc-pdf-calculation-page__column-heading {
	font-weight: 700;
	margin-bottom: 10px;
}

.calc-pdf-calculation-page__list {
	margin: 0;
	padding-left: 22px;
}

.calc-pdf-calculation-page__list li {
	margin-bottom: 4px;
}

.calc-pdf-calculation-page__footnote {
	font-size: 15px;
	line-height: 1.4;
	color: var(--text-dark-muted);
	margin: 0;
}

.calc-pdf-calculation-page__footer {
	position: absolute;
	left: 12px;
	right: 80px;
	bottom: 28px;
	display: flex;
	align-items: center;
	gap: 56px;
	font-size: 17px;
}

.calc-pdf-calculation-page__page-number {
	font-weight: 500;
}

.calc-pdf-calculation-page__confidentiality {
	flex: 1;
}

.calc-pdf-calculation-page__logo {
	width: 140px;
	height: auto;
}
</style>
